/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import './style/switch.scss';

import React from 'react';
import classNames from 'classnames';
import {isNotNull} from "../util/controlUtil";
import {convertColor, editColor} from "../util/colorUtil";

interface SwitchProps {
    onChange: any,
    onMouseEnter: any,
    onMouseLeave: any,
    value: boolean,
    labels: string[],
    baseColor: string,
    trackColor: string,
    offColor: string,
    id: string,
    containerClass: string,
    labelClass: string,
    switchClass: string,
    trackClass: string,
    mainLabelClass: string;
    padding: 'small' | 'standard' | 'large',
    disabled: boolean,
    isHovered: boolean,
    label: string;
}

export class Switch extends React.Component<SwitchProps> {
    getContainerClass = (): string => {
        const {containerClass, disabled, padding} = this.props, classes = ['switchContainer'];
        if (disabled) classes.push('disabled');

        if (padding === 'small') classes.push('sp');
        else if (padding === 'large') classes.push('lp');

        classes.push(containerClass);
        return classNames(classes);
    };

    getLabelClass = (position: 'left' | 'right'): string => {
        const {labelClass} = this.props;
        return classNames('label', position, labelClass);
    };

    getSwitchClass = (): string => {
        const {switchClass, value} = this.props;
        return classNames('switch', value && 'on', switchClass);
    };

    getTrackClass = (): string => {
        const {trackClass} = this.props;
        return classNames('track', trackClass);
    };

    getSwitchStyle = (): object => {
        const {baseColor, isHovered, value, offColor} = this.props;
        let color;
        if (!value && isNotNull(offColor)) color = convertColor(offColor);
        else {
            color = convertColor(baseColor);
            if (isHovered) color = editColor(-.2, color);
        }
        return {backgroundColor: color};
    };

    getTrackStyle = (): object => {
        const {trackColor} = this.props;
        return {backgroundColor: convertColor(trackColor)};
    };

    getLabel = (position: 'left' | 'right'): string | null => {
        const {labels} = this.props, positions: { [index: string]: number } = {left: 0, right: 1};
        if (!labels.length || labels.length <= positions[position]) return null;
        return labels[positions[position]];
    };

    renderMainLabel = () => {
        const {label, mainLabelClass} = this.props;
        if (!label.length) return null;
        return <label className={classNames('main-label', mainLabelClass)}>{label}:&nbsp;</label>
    };

    renderLabel = (position: 'left' | 'right'): React.ReactNode => {
        const label = this.getLabel(position);
        if (label === null) return null;
        return <label className={this.getLabelClass(position)}>{label}</label>;
    };

    renderSwitch = (): React.ReactNode => {
        const {onChange, onMouseEnter, onMouseLeave} = this.props;
        return (
            <div className='switch-track-container' onClick={onChange} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                <div className={this.getTrackClass()} style={this.getTrackStyle()}/>
                <div className={this.getSwitchClass()} style={this.getSwitchStyle()}/>
            </div>
        )
    };

    render = () => {
        const {id} = this.props;
        return (
            <div className={this.getContainerClass()} id={id}>
                {this.renderMainLabel()}
                {this.renderLabel('left')}
                {this.renderSwitch()}
                {this.renderLabel('right')}
            </div>
        )
    }
}